import { apiFetch } from './api';

/**
 * List all messages across all licences
 */
export function listAllMessages(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.type) queryParams.append('type', params.type);
  if (params.unread) queryParams.append('unread', params.unread);
  if (params.organizationType) queryParams.append('organizationType', params.organizationType);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  
  const queryString = queryParams.toString();
  return apiFetch(`/api/admin/messages${queryString ? `?${queryString}` : ''}`);
}

/**
 * List messages for a specific licence
 */
export function listLicenceMessages(licenceId, params = {}) {
  const queryParams = new URLSearchParams();
  if (params.type) queryParams.append('type', params.type);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  
  const queryString = queryParams.toString();
  return apiFetch(`/api/admin/licences/${licenceId}/messages${queryString ? `?${queryString}` : ''}`);
}

/**
 * Send message from admin
 */
export function sendMessage(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/messages`, {
    method: 'POST',
    body: payload
  });
}

/**
 * Send internal staff message
 */
export function sendInternalMessage(licenceId, payload) {
  return apiFetch(`/api/admin/licences/${licenceId}/messages/internal`, {
    method: 'POST',
    body: payload
  });
}

/**
 * Get unread message count for admin
 */
export function getUnreadMessageCount() {
  return apiFetch('/api/admin/messages/unread-count');
}

/**
 * Mark message as read
 */
export function markMessageAsRead(messageId) {
  return apiFetch(`/api/admin/messages/${messageId}/read`, {
    method: 'PUT'
  });
}

export function listDirectMessages(customerId) {
  return apiFetch(`/api/admin/messages/direct/${customerId}`);
}

export function sendDirectMessage(customerId, payload) {
  return apiFetch(`/api/admin/messages/direct/${customerId}`, {
    method: 'POST',
    body: payload
  });
}

/**
 * Get conversations list (grouped by licence)
 * This aggregates messages to show one thread per licence
 */
export async function getConversations(params = {}) {
  try {
    const response = await listAllMessages({
      ...params,
      limit: 1000 // Get all to group by licence
    });
    
    console.log('getConversations - Raw API response:', response);
    
    // Handle both success:true format and direct data format
    // apiFetch returns the JSON directly, so if backend returns { success: true, data: {...} }
    // then response is { success: true, data: {...} }
    let responseData;
    if (response && response.success !== undefined) {
      // Backend returned { success: true, data: {...} }
      responseData = response.data || response;
    } else if (response && response.data) {
      // Response already has data nested
      responseData = response.data;
    } else {
      // Response is the data directly
      responseData = response;
    }
    
    console.log('getConversations - Processed responseData:', responseData);
    
    if (!responseData) {
      console.warn('getConversations - No responseData');
      return { success: false, data: { conversations: [], stats: { total: 0, unread: 0, unanswered: 0 } } };
    }
    
    const messages = responseData.messages || [];
    console.log('getConversations - Messages found:', messages.length);
    console.log('getConversations - Type requested:', params.type);
    console.log('getConversations - Internal messages:', messages.filter(m => m.isInternal).length);
    
    // Group messages by licenceId (and for internal messages, also by the other staff member)
    const conversationsMap = new Map();
    
    // Get current user ID from auth context (we'll need to pass this or get it from storage)
    // For now, we'll infer it from the messages (messages where toUserId matches are TO current user)
    
    messages.forEach(msg => {
      const directCustomerId =
        msg.directCustomerId?._id || msg.directCustomerId?.toString?.() || msg.directCustomerId;
      if (directCustomerId) {
        const directKey = `direct_${directCustomerId.toString()}`;
        const fromCustomer =
          msg.fromCustomerId && typeof msg.fromCustomerId === 'object' ? msg.fromCustomerId : null;
        const toCustomer =
          msg.toCustomerId && typeof msg.toCustomerId === 'object' ? msg.toCustomerId : null;
        const customerProfile = fromCustomer || toCustomer;
        const customerName = customerProfile
          ? `${customerProfile.firstName || ''} ${customerProfile.lastName || ''}`.trim() || 'Customer'
          : 'Customer';
        const customerEmail = customerProfile?.email || '';

        if (!conversationsMap.has(directKey)) {
          conversationsMap.set(directKey, {
            threadType: 'direct',
            isDirectMessage: true,
            directCustomerId: directCustomerId.toString(),
            licenceId: null,
            licenceName: 'Direct message',
            licenceRefNumber: '—',
            licenceStatus: 'Direct',
            customerName,
            customerEmail,
            customerId: directCustomerId.toString(),
            isInternal: false,
            lastMessage: {
              id: msg._id,
              body: msg.body,
              timestamp: msg.createdAt,
              fromCustomer: !!msg.fromCustomerId,
              fromAdmin: !!msg.fromUserId
            },
            unreadCount: 0,
            messages: []
          });
        }
        const conv = conversationsMap.get(directKey);
        conv.messages.push(msg);
        if (new Date(msg.createdAt) > new Date(conv.lastMessage.timestamp)) {
          conv.lastMessage = {
            id: msg._id,
            body: msg.body,
            timestamp: msg.createdAt,
            fromCustomer: !!msg.fromCustomerId,
            fromAdmin: !!msg.fromUserId
          };
        }
        const isUnread =
          msg.isUnreadForCurrentUser !== undefined
            ? msg.isUnreadForCurrentUser
            : !msg.read && (msg.fromCustomerId || msg.toUserId);
        if (isUnread) conv.unreadCount++;
        return;
      }

      const licenceId = msg.licenceId?._id || msg.licenceId;
      if (!licenceId) return;
      
      const licenceIdStr = licenceId.toString();
      
      // For internal messages, create separate conversations per staff member
      // Group by licenceId + the other staff member's ID
      let conversationKey = licenceIdStr;
      let otherStaffMemberId = null;
      let otherStaffMemberName = null;
      
      if (msg.isInternal) {
        // Determine the other staff member (the one we're chatting with)
        const fromUserId = msg.fromUserId?._id || msg.fromUserId?._id?.toString() || msg.fromUserId?.toString() || msg.fromUserId;
        const toUserId = msg.toUserId?._id || msg.toUserId?._id?.toString() || msg.toUserId?.toString() || msg.toUserId;
        
        // Get reviewer and approver from licence info
        const licenceInfo = typeof msg.licenceId === 'object' && msg.licenceId !== null ? msg.licenceId : {};
        const reviewerId = licenceInfo.reviewerId?._id || licenceInfo.reviewerId?.toString() || licenceInfo.reviewerId;
        const approverId = licenceInfo.approverId?._id || licenceInfo.approverId?.toString() || licenceInfo.approverId;
        
        // Get reviewer and approver details from message (if populated by backend)
        const reviewer = msg.reviewer || licenceInfo.reviewer;
        const approver = msg.approver || licenceInfo.approver;
        
        // Determine the other staff member:
        // - If message is FROM current user (fromUserId), then other is toUserId
        // - If message is TO current user (toUserId), then other is fromUserId
        // - If both exist, create conversation key with both IDs sorted
        // - Also check reviewer/approver to determine who we're chatting with
        if (fromUserId && toUserId) {
          // Both exist - create key with both IDs sorted to ensure same conversation
          const ids = [fromUserId.toString(), toUserId.toString()].sort();
          conversationKey = `${licenceIdStr}_${ids[0]}_${ids[1]}`;
          
          // Determine other staff member: use the one that's not the current user
          // Since backend filters to show messages where current user is involved,
          // we'll use toUserId as the other (assuming current user is fromUserId)
          // But we'll also check reviewer/approver to be more accurate
          if (reviewerId && (reviewerId.toString() === fromUserId.toString() || reviewerId.toString() === toUserId.toString())) {
            otherStaffMemberId = reviewerId.toString() === fromUserId.toString() ? toUserId.toString() : fromUserId.toString();
            if (reviewer) {
              otherStaffMemberName = `${reviewer.firstName || ''} ${reviewer.lastName || ''}`.trim() || reviewer.email || 'Reviewer';
            }
          } else if (approverId && (approverId.toString() === fromUserId.toString() || approverId.toString() === toUserId.toString())) {
            otherStaffMemberId = approverId.toString() === fromUserId.toString() ? toUserId.toString() : fromUserId.toString();
            if (approver) {
              otherStaffMemberName = `${approver.firstName || ''} ${approver.lastName || ''}`.trim() || approver.email || 'Approver';
            }
          } else {
            // Fallback: use toUserId as other (assuming current user sent the message)
            otherStaffMemberId = toUserId.toString();
            if (msg.toUserId && typeof msg.toUserId === 'object') {
              otherStaffMemberName = `${msg.toUserId.firstName || ''} ${msg.toUserId.lastName || ''}`.trim() || msg.toUserId.email || 'Staff';
            }
          }
        } else if (fromUserId) {
          conversationKey = `${licenceIdStr}_staff_${fromUserId.toString()}`;
          otherStaffMemberId = fromUserId.toString();
          if (msg.fromUserId && typeof msg.fromUserId === 'object') {
            otherStaffMemberName = `${msg.fromUserId.firstName || ''} ${msg.fromUserId.lastName || ''}`.trim() || msg.fromUserId.email || 'Staff';
          }
        } else if (toUserId) {
          conversationKey = `${licenceIdStr}_staff_${toUserId.toString()}`;
          otherStaffMemberId = toUserId.toString();
          if (msg.toUserId && typeof msg.toUserId === 'object') {
            otherStaffMemberName = `${msg.toUserId.firstName || ''} ${msg.toUserId.lastName || ''}`.trim() || msg.toUserId.email || 'Staff';
          }
        }
        
        // If we still don't have a name, try to get it from reviewer/approver
        if (!otherStaffMemberName) {
          if (reviewer && (reviewer._id?.toString() === otherStaffMemberId || reviewer._id === otherStaffMemberId)) {
            otherStaffMemberName = `${reviewer.firstName || ''} ${reviewer.lastName || ''}`.trim() || reviewer.email || 'Reviewer';
          } else if (approver && (approver._id?.toString() === otherStaffMemberId || approver._id === otherStaffMemberId)) {
            otherStaffMemberName = `${approver.firstName || ''} ${approver.lastName || ''}`.trim() || approver.email || 'Approver';
          }
        }
      }
      
      if (!conversationsMap.has(conversationKey)) {
        // Get licence info from message
        const licenceInfo = typeof msg.licenceId === 'object' && msg.licenceId !== null ? msg.licenceId : {};
        // Get COA name from populated licenceId.coaId
        const coaId = licenceInfo?.coaId;
        const coaName = (coaId && typeof coaId === 'object' && coaId !== null && coaId.name) 
                       ? coaId.name
                       : (msg.licenceName || 'Unknown Licence');
        
        conversationsMap.set(conversationKey, {
          licenceId: licenceIdStr,
          licenceName: coaName,
          licenceRefNumber: licenceInfo.refNumber || 'N/A',
          licenceStatus: licenceInfo.status || msg.licenceStatus || 'N/A',
          // For internal messages, show staff member name; for customer messages, show customer name
          customerName: msg.isInternal 
            ? (otherStaffMemberName || 'Staff Member')
            : ((msg.fromCustomerId && typeof msg.fromCustomerId === 'object' && msg.fromCustomerId !== null) ? 
              `${msg.fromCustomerId.firstName || ''} ${msg.fromCustomerId.lastName || ''}`.trim() || 'Customer' : 
              (msg.toCustomerId && typeof msg.toCustomerId === 'object' && msg.toCustomerId !== null) ?
                `${msg.toCustomerId.firstName || ''} ${msg.toCustomerId.lastName || ''}`.trim() || 'Customer' : 
                'Unknown'),
          customerEmail: msg.isInternal 
            ? (msg.fromUserId?.email || msg.toUserId?.email || '')
            : ((msg.fromCustomerId && typeof msg.fromCustomerId === 'object' && msg.fromCustomerId !== null ? msg.fromCustomerId.email : null) || 
              (msg.toCustomerId && typeof msg.toCustomerId === 'object' && msg.toCustomerId !== null ? msg.toCustomerId.email : null) || ''),
          customerId: msg.isInternal
            ? null
            : ((msg.fromCustomerId && typeof msg.fromCustomerId === 'object' && msg.fromCustomerId !== null ? (msg.fromCustomerId._id || msg.fromCustomerId) : msg.fromCustomerId) ||
              (msg.toCustomerId && typeof msg.toCustomerId === 'object' && msg.toCustomerId !== null ? (msg.toCustomerId._id || msg.toCustomerId) : msg.toCustomerId)),
          // Staff member info for internal messages
          staffMemberId: otherStaffMemberId,
          staffMemberName: otherStaffMemberName,
          isInternal: !!msg.isInternal, // Explicitly set based on message type
          lastMessage: {
            id: msg._id,
            body: msg.body,
            timestamp: msg.createdAt,
            fromCustomer: !!msg.fromCustomerId,
            fromAdmin: !!msg.fromUserId
          },
          unreadCount: 0,
          messages: []
        });
      }
      
      const conv = conversationsMap.get(conversationKey);
      conv.messages.push(msg);
      
      // Update conversation's isInternal flag if this message is internal
      // A conversation is internal if ANY message in it is internal
      if (msg.isInternal) {
        conv.isInternal = true;
      }
      
      // Update last message if this is newer
      if (new Date(msg.createdAt) > new Date(conv.lastMessage.timestamp)) {
        conv.lastMessage = {
          id: msg._id,
          body: msg.body,
          timestamp: msg.createdAt,
          fromCustomer: !!msg.fromCustomerId,
          fromAdmin: !!msg.fromUserId
        };
      }
      
      // Count unread (messages to current admin that are unread)
      // For internal messages, check if toUserId matches and message is unread
      const isDirectMsg = !!msg.directCustomerId;
      const isUnread = msg.isUnreadForCurrentUser !== undefined
        ? msg.isUnreadForCurrentUser
        : isDirectMsg
          ? !msg.read && !!msg.fromCustomerId && !msg.isInternal
          : msg.isInternal
            ? msg.toUserId && !msg.read
            : !msg.read && (msg.toUserId || msg.fromCustomerId);
      
      if (isUnread) {
        conv.unreadCount++;
      }
    });
    
    // Convert map to array and sort by last message time
    const conversations = Array.from(conversationsMap.values())
      .map(conv => {
        // Sort messages by time
        conv.messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        
        // Ensure isInternal is set correctly based on messages
        // If any message is internal, the conversation is internal
        const hasInternalMessage = conv.messages.some(m => m.isInternal === true);
        if (hasInternalMessage) {
          conv.isInternal = true;
        } else if (params.type === 'internal') {
          // If we're loading internal messages and this conversation has no internal messages,
          // it shouldn't be here, but mark it as internal anyway
          conv.isInternal = true;
        }
        
        // Debug logging
        if (conv.isInternal) {
          console.log('Conversation marked as internal:', conv.licenceId, conv.staffMemberName);
        }
        
        return conv;
      })
      .sort((a, b) => new Date(b.lastMessage.timestamp) - new Date(a.lastMessage.timestamp));
    
    const directConversations = conversations.filter((c) => c.isDirectMessage);
    const applicantConversations = conversations.filter(
      (c) => !c.isDirectMessage && !c.isSupportTicket
    );

    // Calculate stats
    const stats = {
      total: conversations.length,
      unread: conversations.filter(c => c.unreadCount > 0).length,
      unanswered: conversations.filter(c => {
        const lastMsg = c.messages[c.messages.length - 1];
        return lastMsg && lastMsg.fromCustomerId && !lastMsg.read;
      }).length,
      directUnread: directConversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
      directTotal: directConversations.length
    };
    
    return {
      success: true,
      data: {
        conversations,
        stats
      }
    };
  } catch (error) {
    console.error('Error getting conversations:', error);
    return { success: false, data: { conversations: [], stats: { total: 0, unread: 0, unanswered: 0 } } };
  }
}

